import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  XMarkIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  UsersIcon,
  CurrencyPoundIcon,
  UserIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { buildApiUrl } from '../config/api';

interface Activity {
  id: string;
  name: string;
  description: string;
  price: number;
  duration?: number;
  maxCapacity?: number;
  venue?: {
    name: string;
    address: string;
  };
}

interface Venue {
  id: string;
  name: string;
  address: string;
  city: string;
}

const WidgetPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const venueId = searchParams.get('venueId');
  const activityId = searchParams.get('activityId');
  const theme = searchParams.get('theme') || 'light';
  const primaryColor = searchParams.get('primaryColor') || '#00806a';
  const isEmbedded = searchParams.get('embedded') === 'true';

  const [venue, setVenue] = useState<Venue | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  useEffect(() => {
    if (venueId) {
      fetchVenueData();
    } else if (activityId) {
      fetchActivityData();
  }

  // Notify parent window that widget is ready
    if (isEmbedded && window.parent !== window) {
      window.parent.postMessage({
        type: 'WIDGET_READY',
        config: { venueId, activityId }
      }, '*');
    }
  }, [venueId, activityId, isEmbedded]);

  const fetchVenueData = async () => {
    try {
      setLoading(true);
      
      // Fetch venue and activities from widget endpoint (public, no auth required)
      const response = await fetch(buildApiUrl(`/widget?venueId=${venueId}`));
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          if (data.data.venue) {
            setVenue(data.data.venue);
          }
          if (data.data.activities) {
            setActivities(data.data.activities || []);
          }
        }
      } else {
        throw new Error('Failed to fetch venue data');
      }
    } catch (error) {
      console.error('Error fetching venue data:', error);
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityData = async () => {
    try {
      setLoading(true);
      // Fetch activity from widget endpoint (public, no auth required)
      const response = await fetch(buildApiUrl(`/widget?activityId=${activityId}`));
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data && data.data.activity) {
          const activity = data.data.activity;
          setSelectedActivity(activity);
          if (activity.venue) {
            setVenue(activity.venue);
          }
        }
      } else {
        throw new Error('Failed to fetch activity');
      }
    } catch (error) {
      console.error('Error fetching activity:', error);
      toast.error('Failed to load activity');
    } finally {
      setLoading(false);
    }
  };

  const handleActivitySelect = (activity: Activity) => {
    setSelectedActivity(activity);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch(buildApiUrl('/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('bookon_token', data.data.tokens.accessToken);
        localStorage.setItem('bookon_user', JSON.stringify(data.data.user));
        setShowLogin(false);
        toast.success('Logged in successfully!');
        window.location.reload();
    } else {
        toast.error('Invalid email or password');
      }
    } catch (error) {
      toast.error('Login failed');
    }
  };

  const handleClose = () => {
    if (isEmbedded && window.parent !== window) {
      window.parent.postMessage({ type: 'WIDGET_CLOSED' }, '*');
    }
  };

  const isDark = theme === 'dark';
  const bgColor = isDark ? 'bg-gray-900' : 'bg-white';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const cardBg = isDark ? 'bg-gray-800' : 'bg-white';

  if (loading) {
    return (
      <div className={`min-h-screen ${bgColor} ${textColor} flex items-center justify-center`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: primaryColor }}></div>
          <p>Loading activities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bgColor} ${textColor} p-4`} style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Header */}
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            {venue && (
              <h1 className="text-2xl font-bold" style={{ color: primaryColor }}>
                {venue.name}
              </h1>
            )}
            <p className="text-sm opacity-75">Book an Activity</p>
          </div>
          {isEmbedded && (
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Login Section */}
        {!localStorage.getItem('bookon_token') && !showLogin && (
          <Card className={`${cardBg} mb-6 p-4`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium mb-1">Sign in to book activities</p>
                <p className="text-sm opacity-75">Create an account or log in to continue</p>
              </div>
              <Button
                onClick={() => setShowLogin(true)}
                style={{ backgroundColor: primaryColor }}
                className="text-white"
              >
                <UserIcon className="w-4 h-4 mr-2" />
                Sign In
              </Button>
            </div>
          </Card>
        )}

        {/* Login Form */}
        {showLogin && (
          <Card className={`${cardBg} mb-6 p-6`}>
            <h2 className="text-xl font-bold mb-4">Sign In</h2>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="email"
                placeholder="Email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                required
              />
              <Input
                type="password"
                placeholder="Password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                required
              />
              <div className="flex gap-2">
                <Button
                  type="submit"
                  style={{ backgroundColor: primaryColor }}
                  className="text-white flex-1"
                >
                  <LockClosedIcon className="w-4 h-4 mr-2" />
                  Sign In
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowLogin(false)}
                >
                  Cancel
                </Button>
              </div>
              <p className="text-sm text-center opacity-75">
                Don't have an account?{' '}
                <a href={`https://bookon.app/register?redirect=/widget?venueId=${venueId}&embedded=true`} target="_blank" className="underline" style={{ color: primaryColor }}>
                  Sign up here
                </a>
              </p>
            </form>
          </Card>
        )}

        {/* Activities List */}
        {!selectedActivity && activities.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold mb-4">Available Activities</h2>
            {activities.map((activity) => (
              <Card
                key={activity.id}
                className={`${cardBg} p-6 cursor-pointer hover:shadow-lg transition-shadow`}
                onClick={() => handleActivitySelect(activity)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2">{activity.name}</h3>
                    {activity.description && (
                      <p className="text-sm opacity-75 mb-3 line-clamp-2">{activity.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-sm">
                      {activity.duration && (
                        <div className="flex items-center gap-1">
                          <ClockIcon className="w-4 h-4" />
                          <span>{activity.duration} min</span>
                        </div>
                      )}
                      {activity.maxCapacity && (
                        <div className="flex items-center gap-1">
                          <UsersIcon className="w-4 h-4" />
                          <span>Max {activity.maxCapacity}</span>
                        </div>
                      )}
                      {activity.venue && (
                        <div className="flex items-center gap-1">
                          <MapPinIcon className="w-4 h-4" />
                          <span>{activity.venue.name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <div className="text-2xl font-bold" style={{ color: primaryColor }}>
                      £{Number(activity.price).toFixed(2)}
                    </div>
                    <Button
                      style={{ backgroundColor: primaryColor }}
                      className="text-white mt-2"
                      size="sm"
                    >
                      Book Now
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Selected Activity Details */}
        {selectedActivity && (
          <Card className={`${cardBg} p-6`}>
            <button
              onClick={() => setSelectedActivity(null)}
              className="mb-4 text-sm opacity-75 hover:opacity-100"
            >
              ← Back to Activities
            </button>
            <h2 className="text-2xl font-bold mb-4">{selectedActivity.name}</h2>
            {selectedActivity.description && (
              <p className="mb-6 opacity-75">{selectedActivity.description}</p>
            )}
            
            <div className="space-y-4 mb-6">
              <div className="flex items-center gap-2">
                <CurrencyPoundIcon className="w-5 h-5" style={{ color: primaryColor }} />
                <span className="text-2xl font-bold" style={{ color: primaryColor }}>
                  £{Number(selectedActivity.price).toFixed(2)}
                </span>
              </div>
              {selectedActivity.duration && (
                <div className="flex items-center gap-2">
                  <ClockIcon className="w-5 h-5 opacity-50" />
                  <span>Duration: {selectedActivity.duration} minutes</span>
                </div>
              )}
              {selectedActivity.venue && (
                <div className="flex items-center gap-2">
                  <MapPinIcon className="w-5 h-5 opacity-50" />
                  <span>{selectedActivity.venue.address}</span>
                </div>
              )}
            </div>

            {localStorage.getItem('bookon_token') ? (
              <Button
                style={{ backgroundColor: primaryColor }}
                className="text-white w-full"
                onClick={() => {
                  window.location.href = `https://bookon.app/activities/${selectedActivity.id}/book?embedded=true`;
                }}
              >
                Continue to Booking
              </Button>
            ) : (
              <Card className={`${cardBg} p-4 mb-4`}>
                <p className="text-sm mb-3">Please sign in to book this activity</p>
                <Button
                  onClick={() => setShowLogin(true)}
                  style={{ backgroundColor: primaryColor }}
                  className="text-white w-full"
                >
                  Sign In to Book
                </Button>
              </Card>
            )}
          </Card>
        )}

        {/* No Activities */}
        {!selectedActivity && activities.length === 0 && !loading && (
          <Card className={`${cardBg} p-12 text-center`}>
            <CalendarIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium mb-2">No activities available</h3>
            <p className="opacity-75">Check back later for new activities at this venue.</p>
          </Card>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-sm opacity-60">Powered by <span style={{ color: primaryColor, fontWeight: 600 }}>BookOn</span></p>
        </div>
      </div>
    </div>
  );
};

export default WidgetPage;
